// Single-snapshot detail (A/S/U), with the immutable original blob, the
// amendment chain, and the projected `current` view (original ⊕ overlay).
// Admins see the Amend button.

import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { snapshots as snapshotsApi } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { formatBalance as formatMoney } from '@/lib/format';
import { AmendDialog } from './amend-dialog';
import type {
  MonthlySnapshotDetail,
  SnapshotAmendmentRow,
  SnapshotAmendmentsResponse,
} from '@frt/shared-types';

export function SnapshotDetailPage() {
  const { data: me } = useMe();
  const { id: idStr } = useParams({ from: '/snapshots/snapshot/$id' as never }) as { id: string };
  const id = Number(idStr);
  const [amendOpen, setAmendOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['snapshot', id, 'detail'] as const,
    queryFn: () => snapshotsApi.detail(id),
  });
  const amendmentsQuery = useQuery({
    queryKey: ['snapshot', id, 'amendments'] as const,
    queryFn: () => snapshotsApi.amendments(id),
  });

  const isAdmin = me?.role === 'admin';

  if (detailQuery.isLoading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (detailQuery.error) {
    return (
      <div className="text-sm text-red-600">
        Failed to load snapshot: {(detailQuery.error as Error).message}
      </div>
    );
  }
  const detail = detailQuery.data as MonthlySnapshotDetail;
  const amendments = (amendmentsQuery.data as SnapshotAmendmentsResponse | undefined)?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Snapshot — <span className="font-mono">{detail.account_display_name}</span>
          </h1>
          <p className="text-sm text-slate-500">
            Period <span className="font-mono">{detail.period}</span> · frozen{' '}
            {new Date(detail.frozen_at).toLocaleString()}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setAmendOpen(true)}>Amend</Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Original (immutable)</CardTitle>
          </CardHeader>
          <CardBody>
            <KV label="Balance">{formatMoney(detail.balance_amount)}</KV>
            <KV label="Source">{detail.balance_source}</KV>
            <KVSection title="Metadata snapshot (as-of-then)" />
            <KV label="Tester">{String(detail.metadata_snapshot.tester_name ?? '')}</KV>
            <KV label="Customer">
              {detail.metadata_snapshot.frt_customer_code} —{' '}
              {String(detail.metadata_snapshot.frt_customer_name ?? '')}
            </KV>
            <KV label="Account type">{String(detail.metadata_snapshot.account_type ?? '')}</KV>
            <KV label="Product type">{String(detail.metadata_snapshot.product_type ?? '')}</KV>
            <KV label="Category">
              {String(detail.metadata_snapshot.frt_category ?? '—')}
            </KV>
            <KV label="Tiller status">{detail.metadata_snapshot.tiller_status}</KV>
            <KV label="On-point tester">
              {detail.metadata_snapshot.on_point_tester_name ?? '—'}
            </KV>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current (with amendments)</CardTitle>
          </CardHeader>
          <CardBody>
            <KV label="Balance">{formatMoney(detail.current.balance_amount)}</KV>
            <KV label="Source">{detail.current.balance_source}</KV>
            <KVSection title="Effective metadata (overlay)" />
            <KV label="Tester">{String(detail.current.metadata_snapshot.tester_name ?? '')}</KV>
            <KV label="Customer">
              {detail.current.metadata_snapshot.frt_customer_code} —{' '}
              {String(detail.current.metadata_snapshot.frt_customer_name ?? '')}
            </KV>
            <KV label="Account type">
              {String(detail.current.metadata_snapshot.account_type ?? '')}
            </KV>
            <KV label="Product type">
              {String(detail.current.metadata_snapshot.product_type ?? '')}
            </KV>
            <KV label="Category">
              {String(detail.current.metadata_snapshot.frt_category ?? '—')}
            </KV>
            <KV label="Tiller status">{detail.current.metadata_snapshot.tiller_status}</KV>
            <KV label="On-point tester">
              {detail.current.metadata_snapshot.on_point_tester_name ?? '—'}
            </KV>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Amendment chain</CardTitle>
        </CardHeader>
        <CardBody>
          {amendments.length === 0 ? (
            <p className="text-sm text-slate-500">No amendments applied.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Field</th>
                    <th className="py-2 pr-3">Old</th>
                    <th className="py-2 pr-3">New</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2 pr-3">By</th>
                  </tr>
                </thead>
                <tbody>
                  {amendments.map((a: SnapshotAmendmentRow) => (
                    <tr key={a.id} className="border-t border-slate-100 align-top">
                      <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">
                        {new Date(a.amended_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 font-mono">{a.field}</td>
                      <td className="py-2 pr-3 font-mono">{a.old_value ?? '—'}</td>
                      <td className="py-2 pr-3 font-mono">{a.new_value ?? '—'}</td>
                      <td className="py-2 pr-3">{a.reason}</td>
                      <td className="py-2 pr-3 text-slate-600">user #{a.amended_by_user_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {isAdmin && amendOpen && (
        <AmendDialog
          snapshotId={id}
          onClose={() => setAmendOpen(false)}
          currentBalance={detail.current.balance_amount}
          currentSource={detail.current.balance_source}
          currentMetadata={detail.current.metadata_snapshot}
        />
      )}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span className="w-32 shrink-0 text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <span className="text-sm text-slate-900">{children}</span>
    </div>
  );
}

function KVSection({ title }: { title: string }) {
  return (
    <div className="mt-3 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
      {title}
    </div>
  );
}

// Whole-dollar balance formatting comes from `@/lib/format` (imported above
// as `formatMoney`).
